import React, { useEffect, useState } from 'react';
import { Button, TextField, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { useMutation, useLazyQuery } from "@apollo/client";
import sitemeta from '../json/site-meta.json';
import { validateEnteredData } from './validate';
import WarningDialog from './WarningDialog'; 
import UserConfirm from './UserConfirm';
import { CREATE_REPORT, DELETE_REPORT, UPDATE_REPORT, ALL_REPORTS } from './queries';
import { weekNumber } from './weekNumber';

function UserInput(props) {
	const { updateUserInputStatus, updateUserEntryStatus, userEntryData } = props;
	const { coopid, ids, obsdate, weekChecked } = userEntryData;
	const [ enteredData, setEnteredData ] = useState();
	const [ entryErrors, setEntryErrors ] = useState({});
	const [ entryWarnings, setEntryWarnings ] = useState({});
	const [ errorFields, setErrorFields ] = useState({});
	const [ dialogOpen, setDialogOpen ] = useState(false);
	const [ showConfirm, setShowConfirm ] = useState(false);
	const [ filterChecked, setFilterChecked ] = useState(false);
	const [ filteredKeys, setFilteredKeys ] = useState([]);
	const entryDate = new Date().toISOString().substr(0, 16);

	// data date range
    const wkfirst = weekChecked ? new Date(obsdate.getTime() - (obsdate.getDay() * 24 * 60 * 60 * 1000)) : obsdate;
    const wklast = weekChecked ? new Date(obsdate.getTime() + ((6-obsdate.getDay()) * 24 * 60 * 60 * 1000)) : obsdate;

	// default data for new entry
	const defaultEntry = {
		year:obsdate.getFullYear().toString(), 
		month:(obsdate.getMonth()+1).toString(), 
		day:obsdate.getDate().toString(), 
		hour:"12", 
		snwd:"", 
		sweq:"",
		notes:"",
		creationDate:"",
	};

	// fauna queries and mutations
	//Can't use ALL_REPORT_FOR_DATE because date may be changed and all reports are needed to do duplicate check
	//const [ getAllReports, {data:allData} ] = useLazyQuery(weekChecked ? ALL_REPORTS : ALL_REPORTS_FOR_DATE, { 
	const [ getAllReports, {data:allData, loading:allLoading} ] = useLazyQuery(ALL_REPORTS, { 
		variables: { year:obsdate.getFullYear().toString(), month:(obsdate.getMonth()+1).toString(), day:obsdate.getDate().toString() },  //not used for ALL_REPORTS
		fetchPolicy: "network-only", 
		onError: (error) => { alert('Error reading existing data - ' + error.graphQLErrors[0].message) },
		onCompleted: (allData) => {
			// add existing data to page and also save in existingData element of enteredData object
//			(weekChecked ? allData.allReports.data : allData.allReportsForDate.data).forEach((entry) => {
			allData.allReports.data.forEach((entry) => {
				const entryDate = new Date(entry.year,entry.month-1,entry.day);
				if (ids.includes(entry.nwsli) && entryDate >= wkfirst && entryDate <= wklast) {
					let ymd = entry.year + entry.month.padStart(2, "0") + entry.day.padStart(2, "0");
					setEnteredData((prevState) => ({...prevState, [entry.nwsli+"_"+ymd]:{...entry, existingData:entry}}));
				}
			});
		  }
	});
	const [ createReport, { createloading } ] = useMutation(CREATE_REPORT, {
//		refetchQueries: [ { query: ALL_REPORTS } ],
		onError(error) { alert('Report creation error - ' + error.graphQLErrors[0].message + '; may be able to try again.') },
	});
	const [ updateReport, { updateloading } ] = useMutation(UPDATE_REPORT, {
//		refetchQueries: [ { query: ALL_REPORTS } ],
		onError(error) { alert('Report update error - ' + error.graphQLErrors[0].message) },
	});
	const [deleteReport, { loading: deleteLoading }] = useMutation(DELETE_REPORT, {
//		refetchQueries: [ { query: ALL_REPORTS } ],
		onError(error) { alert('Report deletion error - ' + error.graphQLErrors[0].message) },
	});

	const duplicateCheck = (stndata) => {
		let isDuplicate = false;
		const { nwsli, year, month, day } = stndata;
//		(weekChecked ? allData.allReports.data : allData.allReportsForDate.data).forEach((rec) => {
		allData.allReports.data.forEach((rec) => {
			if (nwsli === rec.nwsli && year === rec.year && month === rec.month && day === rec.day) {
				isDuplicate = true;
				alert('Ignoring entry for ' + nwsli + " " + [year,month,day].join("-") + 
					': duplicate report - ' + JSON.stringify(rec, ['creationDate','group','hour','snwd','sweq','notes'], 1));
			}
		});
		return isDuplicate;
	};

	// create/delete/update data in database as necessary, then show confirmation page
	const proccessEnteredData = () => {
		for (const [key,value] of Object.entries(enteredData)) {
			if (!value._id) {
				if (value.snwd.length || value.sweq.length || value.notes.length) {
					// add creationDate to each new station record and save to database
					const isDuplicate = duplicateCheck(value);
					if (isDuplicate) {
						setEnteredData((prevState) => {
							const newData = {...prevState}
							delete newData[key]
							return newData;
						});
					} else {
						const week = weekNumber(parseInt(value.year), parseInt(value.month), parseInt(value.day));
						createReport({ variables: { data: {...value, week:week, creationDate:entryDate} }});
						setEnteredData((prevState) => ({...prevState, [key]:{...prevState[key], creationDate:entryDate}}));
					}
				}
			} else {
				// check for updated data for existing reports
				if (!value.snwd.length && !value.sweq.length && !value.notes.length) {
					// user manually deleted all values, so delete entry from database
					deleteReport({ variables: { id: value._id } });				
				} else if (value.year !== value.existingData.year || value.month !== value.existingData.month || value.day !== value.existingData.day) {
					// *** I don't think this can happend any more ***
					// date has changed from existing record, so save it as a new record for that date in database
					delete value.__typename;
					delete value._id;
					delete value.existingData;
					const isDuplicate = duplicateCheck(value);
					if (isDuplicate) {
						setEnteredData((prevState) => {
							const newData = {...prevState}
							delete newData[key]
							return newData;
						});
					} else {
						const week = weekNumber(parseInt(value.year), parseInt(value.month), parseInt(value.day));
						createReport({ variables: { data: {...value, week:week, creationDate:entryDate} }});
						setEnteredData((prevState) => ({...prevState, [key]:{...prevState[key], creationDate:entryDate}}));
					}
				} else if (value.snwd !== value.existingData.snwd || value.sweq !== value.existingData.sweq || value.notes !== value.existingData.notes || value.hour !== value.existingData.hour) {
					// one or more data values have changed in existing record, update in database
					const dbid = value._id;
					delete value.__typename;
					delete value._id;
					delete value.existingData;
					updateReport({ variables: { id: dbid, data: {...value, creationDate:entryDate}}});
					setEnteredData((prevState) => ({...prevState, [key]:{...prevState[key], creationDate:entryDate}}));
				}	
			}
		}
		// hide input and show confirmation
		setShowConfirm(true);
	};

	// save key entered data values
	const updateData = (nwsli, event) => {
		const value = event.target.value;
		const name = event.target.name;
		setEnteredData((prevState) => ({...prevState, [nwsli]:{...prevState[nwsli], [name]: value}}));
	};

	// delete a station from database and reinitialize station on current page (removing old database id and existing data)
	const handleDelete = (id, e) => {
		e.preventDefault();
		deleteReport({ variables: { id: enteredData[id]._id } });
		if (!weekChecked) {
			// reinitialize entry on current page for station to default
			const [nwsli, ] = id.split("_");
			setEnteredData((prevState) => ({...prevState, [id]:{ ...defaultEntry, nwsli:nwsli, altid:sitemeta[nwsli].altid, name:sitemeta[nwsli].name, group:coopid}}));
		} else {
			// remove entry from page
			setEnteredData((prevState) => {
				const newData = {...prevState}
				delete newData[id]
				return newData;
			});
		}
	};

	// submit entered data
	const handleClick = () => {	
		const validation = validateEnteredData(enteredData);
		setEntryErrors(validation.errors);
		setEntryWarnings(validation.warnings);
		setErrorFields(validation.errorFields);
		if (Object.entries(validation.errors).length === 0 && validation.warnings.length === 0) {
			proccessEnteredData();
		} else if (Object.entries(validation.errors).length === 0 && validation.warnings.length > 0) {
			setDialogOpen(true);
		}
	};

	// submit entered data
	const handleCancelClick = () => {	
		updateUserInputStatus(false);
		updateUserEntryStatus(true);
	};

	// handle filtering checkbox change
	const handleFilterChange = (event) => {
		const checked = event.target.checked;
		setFilterChecked(checked);
		// create list of most recent key for each site, with those having exising data taking precedence over empty entries
		if (checked) {
			const fkeys = [];
			const nwslis = {};
			// compile all dates for each nwsli
			Object.keys(enteredData).forEach((key) => {
				const [nwsli, ymd] = key.split("_");
				if (!Object.keys(nwslis).includes(nwsli)) {
					nwslis[nwsli] = [];
				} 
				nwslis[nwsli].push(ymd);
			});
			// pick out latest date (preferably with existing data) for each site and save reconstructed key
			Object.keys(nwslis).forEach((id) => {
				if (nwslis[id].length === 1) {
					const skey = id + "_" + nwslis[id][0];
					fkeys.push(skey)
				} else {
					let saved_key = id + "_" + nwslis[id][-1];
					nwslis[id].sort((a, b) => a > b ? 1 : -1).forEach((dt) => {
						let testkey = id + "_" + dt;
						if (enteredData[testkey].hasOwnProperty('existingData')) {
							saved_key = testkey;
						}
					});
					fkeys.push(saved_key);
				}
			});
			setFilteredKeys(fkeys);
		}
	};

	// sort enteredData keys by nwsli custom order
	const sortKeys = (keyArray) => {
		keyArray.sort((a, b) => a > b ? 1 : -1);  //sorts by nwsli and date
//		keyArray.sort(function(a, b) {			  //sorts by custom nwsli order (retaining date order within nwsli)
//			const nwslia = a.split("_")[0];
//			const nwslib = b.split("_")[0];
//			return ids.indexOf(nwslia) - ids.indexOf(nwslib);
//		});
		return keyArray;
	};

	// set up default entries for each station and query database for exising data
	useEffect(() => {
		const defaultData = {};
		const ymd = defaultEntry.year + defaultEntry.month.padStart(2, "0") + defaultEntry.day.padStart(2, "0");
		ids.forEach((nwsli) => {
			defaultData[nwsli+"_"+ymd] = {
				...defaultEntry,
				nwsli:nwsli,
				altid:"altid" in sitemeta[nwsli] ? sitemeta[nwsli].altid : "",
				name:sitemeta[nwsli].name, 
				group:coopid,
			};
		});
		setEnteredData(defaultData);
		getAllReports();
	}, [ids]);	// eslint-disable-line react-hooks/exhaustive-deps


	return (
		<>
			{enteredData && !showConfirm &&
				<>
					<Typography variant="subtitle2" gutterBottom>
						Cooperator ID: {coopid}
					</Typography>
					{weekChecked && 
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={filterChecked}
									size="small"
									onChange={handleFilterChange}
								/>
							}
							label="Filter to one entry per site"
						/>
					}
					{allLoading &&
						<Typography variant="body1" color="error" gutterBottom>
							Checking database for existing data ...
						</Typography>
					}
					<form noValidate autoComplete="off">
						<TableContainer >
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell align="center">NWSLI</TableCell>
										<TableCell align="center">Alt ID</TableCell>
										<TableCell align="left">Site name</TableCell>
										<TableCell align="center">Year</TableCell>
										<TableCell align="center">Month</TableCell>
										<TableCell align="center">Day</TableCell>
										<TableCell align="center">Hour (UTC)</TableCell>
										<TableCell align="center">Depth (in)</TableCell>
										<TableCell align="center">SWE (in)</TableCell>
										<TableCell align="center">Notes</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{sortKeys(filterChecked ? filteredKeys : Object.keys(enteredData)).map((id) => 
										<React.Fragment key={id}>
											<TableRow>
												<TableCell align="center">{enteredData[id].nwsli}</TableCell>
												<TableCell align="center">{sitemeta[enteredData[id].nwsli].altid}</TableCell>
												<TableCell align="left">{sitemeta[enteredData[id].nwsli].name}</TableCell>
												<TableCell align="center">
													<TextField
														name="year"
														value={enteredData[id].year}
														error={errorFields[id] && errorFields[id].includes('year')}
														disabled={enteredData[id].hasOwnProperty("existingData")}
														onChange={(e) => updateData(id, e)}
														style={{width:"4em"}}
														inputProps={{ style: {padding:"6px"}, tabIndex: "-1" }}
													/>	
												</TableCell>
												<TableCell align="center">
													<TextField
														name="month"
														value={enteredData[id].month} 
														error={errorFields[id] && errorFields[id].includes('month')}
														disabled={enteredData[id].hasOwnProperty("existingData")}
														onChange={(e) => updateData(id, e)}
														style={{width:"2.5em"}}
														inputProps={{ style: {padding:"6px"}, tabIndex: "-1"  }}
													/>	
												</TableCell>
												<TableCell align="center">
													<TextField
														name="day"
														value={enteredData[id].day} 
														error={errorFields[id] && errorFields[id].includes('day')}
														disabled={enteredData[id].hasOwnProperty("existingData")}
														onChange={(e) => updateData(id, e)}
														style={{width:"2.5em"}}
														inputProps={{ style: {padding:"6px"}, tabIndex: "-1"  }}
													/>	
												</TableCell>
												<TableCell align="center">
													<TextField
														name="hour"
														value={enteredData[id].hour} 
														error={errorFields[id] && errorFields[id].includes('hour')}
														onChange={(e) => updateData(id, e)}
														style={{width:"2.5em"}}
														inputProps={{ style: {padding:"6px"}, tabIndex: "-1"  }}
													/>
												</TableCell>
												<TableCell align="center">
													<TextField
														name="snwd"
														value={enteredData[id].snwd} 
														error={errorFields[id] && errorFields[id].includes('snwd')}
														onChange={(e) => updateData(id, e)}
														style={{width:"4.5em"}}
														inputProps={{ style: {padding:"6px"} }}
													/>
												</TableCell>
												<TableCell align="center">
													<TextField
														name="sweq"
														value={enteredData[id].sweq} 
														error={errorFields[id] && errorFields[id].includes('sweq')}
														onChange={(e) => updateData(id, e)}
														style={{width:"4.5em"}}
														inputProps={{ style: {padding:"6px"} }}
													/>
												</TableCell>
												<TableCell align="left">
													<TextField
														name="notes"
														value={enteredData[id].notes} 
														onChange={(e) => updateData(id, e)}
														inputProps={{ style: {padding:"6px"} }}
													/>
												</TableCell>
												<TableCell align="center">
													{enteredData[id]._id &&
														<Button variant="outlined" color="primary" size="small" disabled={deleteLoading} onClick={(e) => handleDelete(id, e)}>
															Delete
														</Button>
													}
												</TableCell>
											</TableRow>
											{entryErrors[id] && entryErrors[id].length > 0 &&
												<TableRow>
													<TableCell colSpan={10}>
														<Typography color="error">
															{entryErrors[id].reduce((prev, curr) => [ prev, ' ', curr ] )}
														</Typography>
													</TableCell>
												</TableRow>
											}
										</React.Fragment>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						{ Object.entries(entryErrors).length > 0 &&
							<Typography variant="body2" color="error" style={{margin:"1em 0 0.2em 0"}}>
								Correct errors and press Continue
							</Typography>
						}

						{ dialogOpen &&
							<WarningDialog
								warningTitle={"Validation warning"}
								warningText={entryWarnings}
								buttonLabels={["Don't submit","Submit Anyway"]}
								proccessEnteredData={proccessEnteredData}
								open={dialogOpen}
								setOpen={setDialogOpen}
							/>
						}

						<Button variant="contained" color="primary" disabled={createloading || updateloading || deleteLoading} onClick={handleClick}>
							Continue
						</Button>
						<Button variant="contained" color="primary" disabled={createloading || updateloading || deleteLoading} onClick={handleCancelClick}>
							Cancel
						</Button>
					</form>

				</>
			}
			{showConfirm &&
				<UserConfirm
					updateUserInputStatus={updateUserInputStatus}
					updateUserEntryStatus={updateUserEntryStatus}
					enteredData={enteredData}
				/> 
			}
		</>
	);
};
export default UserInput;
